import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {useTranslation} from '@/translations/i18n'
import {capitalize} from '@/utilities/helpers'
import dayjs from 'dayjs'
import {useMemo, useState} from 'react'

export const InputSelectMonthYear = ({
    value,
    label,
    onChange,
    errorMessage,
    yearsFromNow
}: {
    value: Date | null
    label: string
    errorMessage?: string
    onChange: (date: Date) => void
    yearsFromNow?: number
}) => {
    const {
        i18n: {language, t}
    } = useTranslation()
    const [currentMonth, setCurrentMonth] = useState<null | string>(value ? dayjs(value).get('month').toString() : null)
    const [currentYear, setCurrentYear] = useState<null | string>(value ? dayjs(value).get('year').toString() : null)

    const monthOptions = useMemo(() => {
        return Array.from({length: 12}, (_, i) => {
            const monthDate = dayjs().month(i).locale(language)
            return {
                key: i.toString(),
                value: capitalize(monthDate.format('MMMM'))
            }
        }).reduce(
            (acc, month) => ({
                ...acc,
                [month.key]: month.value
            }),
            {}
        )
    }, [language])

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            <Label>{label}</Label>
            <Flexbox fullWidth gap={2}>
                <InputSelect
                    placeholder={t('commons:select_month')}
                    errorMessage={errorMessage && !currentMonth ? errorMessage : ''}
                    sameWidth
                    value={currentMonth != null ? currentMonth : ''}
                    optionsMap={monthOptions}
                    onChange={value => {
                        setCurrentMonth(value)
                        if (currentYear) {
                            onChange(dayjs(new Date(parseInt(currentYear), parseInt(value))).toDate())
                        }
                    }}
                />

                <InputSelectYear
                    value={currentYear ?? ''}
                    errorMessage={errorMessage && !currentYear ? errorMessage : ''}
                    onChange={value => {
                        setCurrentYear(value)
                        if (currentMonth) {
                            onChange(dayjs(new Date(parseInt(value), parseInt(currentMonth))).toDate())
                        }
                    }}
                    yearsFromNow={yearsFromNow}
                />
            </Flexbox>
        </Flexbox>
    )
}

export const InputSelectYear = ({
    value,
    label,
    onChange,
    errorMessage,
    yearsFromNow = 11,
    reverseOrder
}: {
    value?: string
    label?: string
    errorMessage?: string
    onChange: (year: string) => void
    yearsFromNow?: number
    reverseOrder?: boolean
}) => {
    const {
        i18n: {t}
    } = useTranslation()

    const yearOptions = useMemo(() => {
        const currentYear = dayjs().year()
        return Array.from(
            {length: Math.abs(yearsFromNow)},
            (_, i) => currentYear + i * (yearsFromNow < 0 ? -1 : 1)
        ).reduce(
            (acc, year) => ({
                ...acc,
                [year]: year.toString()
            }),
            {}
        )
    }, [yearsFromNow])
    const isValueIncluded = (value: string) => {
        if (!value) return false
        return Object.values(yearOptions).includes(value)
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            {label && <Label>{label}</Label>}
            <InputSelect
                placeholder={t('commons:select_year')}
                errorMessage={errorMessage}
                sameWidth
                value={isValueIncluded(value || '') ? value : ''}
                onChange={onChange}
                optionsMap={yearOptions}
                reverseOrder={reverseOrder}
            />
        </Flexbox>
    )
}
